import { useEffect, useRef, useState } from 'react';
import { TrendUp } from '@phosphor-icons/react';

/**
 * Counts a number up from its previous value to `target` over `durationMs`.
 * Uses requestAnimationFrame so it's smooth without extra deps.
 * Keeps the previous value in a ref so re-renders (filter changes) animate
 * from where they were, not from zero.
 */
function useCountUp(target, durationMs = 900) {
  const [display, setDisplay] = useState(target);
  const fromRef = useRef(target);
  const rafRef = useRef(0);

  useEffect(() => {
    const start = performance.now();
    const from = fromRef.current;
    const to = Number(target) || 0;
    if (from === to) { setDisplay(to); return; }

    const tick = (now) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / durationMs);
      // Ease-out cubic — decelerates as it approaches target
      const eased = 1 - Math.pow(1 - t, 3);
      const value = Math.round(from + (to - from) * eased);
      setDisplay(value);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = to;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, durationMs]);

  return display;
}

/**
 * Grand potential-earning card sitting at the top of the admin Students tab.
 * Admin-only. Recomputes whenever `total` (derived from filtered students) changes.
 */
export function PotentialEarningGrandCard({ total, studentCount, intakeCount }) {
  const displayed = useCountUp(total, 900);
  const hasData = studentCount > 0;

  return (
    <div
      className="relative overflow-hidden border border-gray-200 bg-[#f8f7f4] mb-8"
      data-testid="potential-earning-grand-card"
    >
      {/* Subtle grain — pure CSS, no background image needed */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(#000 1px, transparent 1px)',
          backgroundSize: '3px 3px',
        }}
      />
      <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 px-8 py-7">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] text-gray-500">
            <TrendUp size={11} weight="bold" className="text-emerald-600" />
            Potential Earning
          </div>
          <p
            className="mt-2 text-5xl leading-none font-semibold tabular-nums text-gray-900"
            style={{ fontFamily: "'Libre Baskerville', serif" }}
            data-testid="potential-earning-grand-value"
          >
            ${displayed.toLocaleString()}
          </p>
          <p className="mt-3 text-[12px] text-gray-500">
            {hasData ? (
              <>
                across <span className="tabular-nums font-semibold text-gray-800">{studentCount}</span> live student{studentCount === 1 ? '' : 's'}
                {intakeCount > 0 && (
                  <>
                    {' '}·{' '}
                    <span className="tabular-nums font-semibold text-gray-800">{intakeCount}</span> intake{intakeCount === 1 ? '' : 's'}
                  </>
                )}
              </>
            ) : (
              <>Assign a sale price to students to see potential earning here.</>
            )}
          </p>
        </div>
        <div className="hidden sm:block text-[10px] leading-relaxed text-gray-400 max-w-[220px] text-right">
          Sum of sale price minus college fee &amp; recruiter commission for every non-rejected student in the current filter.
        </div>
      </div>
      {/* Hairline emerald accent bar along the bottom edge */}
      <span aria-hidden className="absolute bottom-0 left-0 h-[2px] w-24 bg-emerald-500/80" />
    </div>
  );
}

/**
 * Compact per-intake potential-earning pill shown inside each accordion
 * header (right-aligned, opposite the intake title/stage chips).
 */
export function PotentialEarningPill({ total }) {
  const displayed = useCountUp(total, 700);
  return (
    <div
      className="flex flex-col items-end pr-4 border-r border-gray-200 mr-4"
      data-testid="potential-earning-pill"
    >
      <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-gray-400">
        Potential
      </span>
      <span
        className="mt-0.5 text-[15px] leading-none font-semibold tabular-nums text-emerald-700"
        data-testid="potential-earning-pill-value"
      >
        ${displayed.toLocaleString()}
      </span>
    </div>
  );
}
